import { ofType } from 'redux-observable';
import {
   filter,
   ignoreElements,
   map,
   mapTo,
   switchMap,
   take,
   tap,
} from 'rxjs/operators';
import { of, merge, from, forkJoin, zip, timer } from 'rxjs';
import { ajax } from 'rxjs/ajax';

import {
   addScore,
   setStage,
   init,
   SUBMIT_ANSWER,
   startNextChallenge,
   COMPLETE_SESSION,
   SET_STAGE,
   setProgress,
   addModule,
   LOAD_MODULE,
   loadModule,
   SET_SELECTED_MODULES,
   replaceExerciseDocument,
   ADVANCE_EXERCISE_STAGE,
   prepareNextSession,
   PREPARE_NEXT_SESSION,
   setModuleLoading,
   REFRESH_MODULE_QUEUE,
   INIT,
   refreshModuleQueue,
   refreshCurrentModule,
   commonExerciseReady,
   COMMON_EXERCISE_READY,
   EXERCISE_READY,
   REFRESH_CURRENT_MODULE,
   AWAIT_TUTORIAL,
   awaitTutorial,
   startNextSession,
   START_NEXT_SESSION,
   prepareNextChallenge,
   PREPARE_NEXT_CHALLENGE,
   commonExerciseChallengeReady,
   COMMON_EXERCISE_CHALLENGE_READY,
   EXERCISE_CHALLENGE_READY,
   START_NEXT_CHALLENGE,
   completeSession,
} from './actions';
import { exerciseStages } from '../shared/constants';
import {
   clearTutorial,
   INITIAL_SYNC_RESPONSE,
   INIT_ACTIVE_EXERCISE,
   setTutorial,
   SET_ASSETS_PERMISSIONS_FROM_SESSION_STORAGE,
} from '../app/actions';
import { COMPLETE_TUTORIAL, INIT as TUTORIAL_INIT } from '../tutorial/actions';
import { assetsUrl, authStatuses } from '../util/constants';
import { getExerciseProgress, putExerciseProgress } from './exerciseData';
import { initialExerciseProgress } from './initialExerciseProgress';
import { calculateScore } from './calculateScore';
import { setCompareAnswer } from '../shared/actions';
import { getCurrentLevel } from './getCurrentLevel';
import { getSubscriptionsFromState } from '../util/getSubscriptionsFromState';
import { checkLoadFromModule } from './checkLoadFromModule';
import { calculateAverageSessionScore } from '../routes/calculateAverageSessionScore';

const syncExercise = (activeExercise, userData) => {
   const isModule =
      activeExercise.exerciseDocument.isModule ||
      activeExercise.currentModuleId;

   let progress;
   let exerciseId;
   let moduleId;

   if (isModule && activeExercise.exerciseDocument.isModule) {
      progress = activeExercise.progress;
      exerciseId = activeExercise.exerciseDocument.mainExerciseId;
      moduleId = activeExercise.exerciseDocument.id;
   } else if (isModule) {
      const currentModule =
         activeExercise.modules[activeExercise.currentModuleId];

      progress = currentModule.progress;
      exerciseId = activeExercise.exerciseDocument.id;
      moduleId = currentModule.exerciseDocument.id;
   } else {
      progress = activeExercise.progress;
      exerciseId = activeExercise.exerciseDocument.id;
   }

   if (userData.status === authStatuses.unauthenticated) {
      // In future, only extend current data if daily routine is active and this is not first exercise in routine, otherwise replace data
      const syncData = JSON.parse(sessionStorage.getItem('syncData')) || {};

      if (isModule) {
         const moduleData = syncData.modules || {};
         if (!moduleData[exerciseId]) {
            moduleData[exerciseId] = {};
         }
         moduleData[exerciseId][moduleId] = progress;
         syncData.modules = moduleData;
      } else {
         const exerciseData = syncData.exercises || {};
         exerciseData[exerciseId] = progress;
         syncData.exercises = exerciseData;
      }

      sessionStorage.setItem('syncData', JSON.stringify(syncData));
   } else if (userData.status === authStatuses.authenticated) {
      putExerciseProgress({
         exerciseId,
         exerciseData: {
            ...progress,
            accuracy: Math.round(
               Math.round(calculateAverageSessionScore(progress.sessionHistory))
            ),
         },
         moduleId: moduleId,
      });
   }
};

const initEpic = (action$, state$) =>
   action$.pipe(
      ofType(INIT_ACTIVE_EXERCISE),
      switchMap((action) => {
         let observable;
         if (
            state$.value.appData.userData.status === authStatuses.authenticated
         ) {
            const syncData = { exerciseId: action.exerciseDocument.id };
            if (action.exerciseDocument.isModule) {
               syncData.exerciseId = action.exerciseDocument.mainExerciseId;
               syncData.moduleId = action.exerciseDocument.id;
            }
            observable = from(
               getExerciseProgress(syncData.exerciseId, syncData.moduleId)
            );
         } else {
            observable = of(initialExerciseProgress);
         }
         return observable.pipe(
            map((exerciseProgress) =>
               init(
                  action.exerciseDocument,
                  exerciseProgress,
                  action.levelSelect,
                  action.nextConfig
               )
            )
         );
      })
   );

const checkSubscription = ({ appData: { permissions } }) => {
   return true;
   const subData = permissions?.subscriptionAndPurchases?.subscription;

   let isSubscribed = false;
   if (subData) {
      if (
         new Date(subData.expiration) > Date.now() &&
         subData.includes.includes('exercise')
      ) {
         isSubscribed = true;
      }
   }
   return isSubscribed;
};
const advanceExerciseEpic = (action$, state$) =>
   action$.pipe(
      ofType(ADVANCE_EXERCISE_STAGE),
      switchMap(() => {
         const currentState = state$.value;
         const { activeExercise } = currentState;

         const isSubscribed = checkSubscription(state$.value);

         const currentExerciseOrModule = activeExercise.currentModuleId
            ? activeExercise.modules[activeExercise.currentModuleId]
            : activeExercise;

         // Complete Session
         if (
            [exerciseStages.viewScore, exerciseStages.compareAnswer].includes(
               activeExercise.currentStage
            ) &&
            activeExercise.scores.length ===
               activeExercise.settings.challengesPerSession
         ) {
            return of(completeSession(isSubscribed));
         }

         // Show Level Up
         if (
            activeExercise.currentStage === exerciseStages.sessionOverview &&
            activeExercise.hasLeveledUp
         ) {
            return of(setStage(exerciseStages.levelUp));
         }

         const level =
            currentState.activeExercise.levelSelect?.level ||
            currentExerciseOrModule.progress.currentLevel;
         const isLoggedIn =
            currentState.appData.userData.status === authStatuses.authenticated;

         // Show Sign Up
         if (
            !isLoggedIn &&
            exerciseStages.welcome === activeExercise.currentStage &&
            currentExerciseOrModule?.exerciseDocument &&
            level >
               currentExerciseOrModule.exerciseDocument.maxUnauthenticatedLevel
         ) {
            return of(setStage(exerciseStages.signUpPrompt));
         }

         // Show Subscribe Prompt
         if (
            [
               exerciseStages.welcome,
               exerciseStages.sessionOverview,
               exerciseStages.signUp,
               exerciseStages.levelUp,
            ].includes(activeExercise.currentStage) &&
            !isSubscribed &&
            currentExerciseOrModule?.exerciseDocument &&
            level > currentExerciseOrModule.exerciseDocument.maxUnpaidLevel
         ) {
            return of(setStage(exerciseStages.subscribePrompt));
         }

         // Show Load Modules
         if (
            [
               exerciseStages.welcome,
               exerciseStages.sessionOverview,
               exerciseStages.levelUp,
               exerciseStages.signUp,
               exerciseStages.subscribePrompt,
            ].includes(activeExercise.currentStage) &&
            checkLoadFromModule(currentState) &&
            !activeExercise.progress.selectedModules?.length
         ) {
            return of(setStage(exerciseStages.selectModules));
         }

         // Prepare Next Session
         if (
            [
               exerciseStages.welcome,
               exerciseStages.sessionOverview,
               exerciseStages.subscribePrompt,
               exerciseStages.levelUp,
               exerciseStages.signUp,
               exerciseStages.selectModules,
            ].includes(activeExercise.currentStage)
         ) {
            return of(prepareNextSession());
         }

         // catch all other cases above, and this is default case
         return of(prepareNextChallenge());
      })
   );

const prepareNextSessionEpic = (action$, state$) =>
   action$.pipe(
      ofType(PREPARE_NEXT_SESSION),
      switchMap(() => {
         const currentState = state$.value;

         // If should load from module, then refresh current module and await that being loaded
         if (checkLoadFromModule(currentState)) {
            return merge(
               of(refreshCurrentModule()),
               action$.pipe(
                  ofType(REFRESH_CURRENT_MODULE),
                  take(1),
                  switchMap(() => timer(0, 100)),
                  filter(() => {
                     const aux =
                        state$.value.activeExercise.modules[
                           state$.value.activeExercise.currentModuleId
                        ];
                     return aux && !aux.loading;
                  }),
                  take(1),
                  mapTo(awaitTutorial())
               )
            );
         }

         return of(awaitTutorial());
      })
   );

const awaitTutorialEpic = (action$, state$) =>
   action$.pipe(
      ofType(AWAIT_TUTORIAL),
      switchMap(() => {
         const currentState = state$.value;
         const moduleId = currentState.activeExercise.currentModuleId;

         const level = getCurrentLevel(currentState, moduleId);

         const exerciseId = currentState.activeExercise.exerciseDocument.id;

         const currentActiveExercise = moduleId
            ? currentState.activeExercise.modules[moduleId]
            : currentState.activeExercise;

         const userIsUnsubscribedAndBeyondMaxUnpaidLevel =
            level < currentActiveExercise.progress.currentLevel;

         const tutorial =
            currentActiveExercise.exerciseDocument.levels[level - 1].tutorialId;

         const { inlineTutorial } =
            currentActiveExercise.exerciseDocument.levels[level - 1];

         const activeSubscriptions = getSubscriptionsFromState(state$.value);

         if (
            (currentState.activeExercise.levelSelect == null &&
               !currentActiveExercise.progress.tutorialComplete &&
               !userIsUnsubscribedAndBeyondMaxUnpaidLevel &&
               !(tutorial == null && inlineTutorial == null)) ||
            currentState.activeExercise.levelSelect?.tutorial
         ) {
            return merge(
               of(
                  setTutorial({
                     inlineTutorial,
                     tutorialId: tutorial,
                     exerciseId,
                     moduleId,
                     secure: !!activeSubscriptions.exercise,
                  })
               ),
               action$.pipe(
                  ofType(TUTORIAL_INIT),
                  take(1),
                  switchMap(() => timer(0, 100)),
                  filter(() => state$.value.tutorial?.ready),
                  take(1),
                  mapTo(commonExerciseReady())
               )
            );
         }

         return of(commonExerciseReady());
      })
   );

const nextSessionReadyEpic = (action$) =>
   action$.pipe(
      ofType(PREPARE_NEXT_SESSION),
      switchMap(() =>
         zip(
            action$.pipe(ofType(COMMON_EXERCISE_READY)),
            action$.pipe(ofType(EXERCISE_READY))
         ).pipe(take(1))
      ),
      mapTo(startNextSession())
   );

const startNextSessionEpic = (action$, state$) =>
   action$.pipe(
      ofType(START_NEXT_SESSION),
      map(() => {
         // if tutorial, do tutorial
         if (state$.value.tutorial != null) {
            return setStage(exerciseStages.tutorial);
         }
         return prepareNextChallenge();
      })
   );

const completeTutorialEpic = (action$, state$) =>
   action$.pipe(
      ofType(COMPLETE_TUTORIAL),
      tap(() => {
         const currentState = state$.value;
         // don't need to sync if they have used level select
         if (currentState.activeExercise.levelSelect == null) {
            syncExercise(
               currentState.activeExercise,
               currentState.appData.userData
            );
         }
      }),
      switchMap(() => merge(of(clearTutorial()), of(prepareNextChallenge())))
   );

const prepareNextChallengeEpic = (action$) =>
   action$.pipe(
      ofType(PREPARE_NEXT_CHALLENGE),
      mapTo(commonExerciseChallengeReady())
   );

const nextChallengeReadyEpic = (action$) =>
   action$.pipe(
      ofType(PREPARE_NEXT_CHALLENGE),
      switchMap(() =>
         zip(
            action$.pipe(ofType(COMMON_EXERCISE_CHALLENGE_READY)),
            action$.pipe(ofType(EXERCISE_CHALLENGE_READY))
         ).pipe(take(1))
      ),
      mapTo(startNextChallenge())
   );

const startNextChallengeEpic = (action$) =>
   action$.pipe(
      ofType(START_NEXT_CHALLENGE),
      mapTo(setStage(exerciseStages.challenge))
   );

const submitAnswerEpic = (action$, state$) =>
   action$.pipe(
      ofType(SUBMIT_ANSWER),
      filter(
         () =>
            !state$.value.activeExercise.exerciseDocument.useCustomSubmitHandler
      ),
      switchMap(() =>
         merge(
            of(
               addScore(
                  calculateScore(
                     state$.value.activeExercise.type,
                     state$.value.activeInterface
                  )
               )
            ),
            of(setStage(exerciseStages.viewScore))
         )
      )
   );

const compareAnswerEpic = (action$) =>
   action$.pipe(
      ofType(SET_STAGE),
      filter(
         (action) =>
            action.stage === exerciseStages.compareAnswer ||
            action.stage === exerciseStages.viewScore
      ),
      switchMap(() =>
         merge(
            of(setCompareAnswer(true)),
            action$.pipe(
               ofType(SET_STAGE),
               filter(
                  (action) =>
                     action.stage !== exerciseStages.compareAnswer &&
                     action.stage !== exerciseStages.viewScore
               ),
               take(1),
               mapTo(setCompareAnswer(false))
            )
         )
      )
   );

const completeSessionEpic = (action$, state$) =>
   action$.pipe(
      ofType(COMPLETE_SESSION),
      tap(() => {
         const currentState = state$.value;

         // don't need to sync if they have used level select
         if (currentState.activeExercise.levelSelect == null) {
            syncExercise(
               currentState.activeExercise,
               currentState.appData.userData
            );
         }
      }),
      ignoreElements()
   );

const loadModuleEpic = (action$, state$) =>
   action$.pipe(
      ofType(LOAD_MODULE),

      filter(
         (action) => state$.value.activeExercise.modules[action.id] == null
      ),
      switchMap((action) => {
         const activeSubscriptions = getSubscriptionsFromState(state$.value);

         return merge(
            of(setModuleLoading(action.id)),
            forkJoin({
               progress: from(
                  getExerciseProgress(
                     state$.value.activeExercise.exerciseDocument.id,
                     action.id
                  )
               ),
               exerciseDocument: ajax({
                  url: `${assetsUrl}/${
                     activeSubscriptions.exercise ? 'secure' : 'public'
                  }/exercise/${
                     state$.value.activeExercise.exerciseDocument.id
                  }/module/${action.id}/exerciseDocument.json`,
                  headers: {
                     Authorization: `Bearer ${state$.value.appData.permissions.token}`,
                  },
                  // withCredentials: true,
                  // crossDomain: true,
               }).pipe(map(({ response }) => response)),
            }).pipe(
               switchMap(({ exerciseDocument, progress }) =>
                  of(
                     addModule({
                        exerciseDocument,
                        progress,
                     })
                  )
               )
            )
         );
      })
   );

const selectModulesEpic = (action$, state$) =>
   action$.pipe(
      ofType(SET_SELECTED_MODULES),
      tap(() =>
         syncExercise(
            state$.value.activeExercise,
            state$.value.appData.userData
         )
      ),
      mapTo(refreshModuleQueue(1, { clearCurrent: true }))
   );

const queueModulesOnInitEpic = (action$, state$) =>
   action$.pipe(
      ofType(INIT),
      filter(() => checkLoadFromModule(state$.value)),
      mapTo(refreshModuleQueue(1))
   );

const queueModulesOnFirstChallengeEpic = (action$, state$) =>
   action$.pipe(
      ofType(START_NEXT_CHALLENGE),
      filter(() => {
         if (!checkLoadFromModule(state$.value)) {
            return false;
         }
         return state$.value.activeExercise.scores.length === 0;
      }),
      mapTo(refreshModuleQueue(1))
   );

const loadQueuedModulesEpic = (action$, state$) =>
   action$.pipe(
      ofType(REFRESH_MODULE_QUEUE),
      map(() =>
         Object.keys(
            state$.value.activeExercise.moduleQueue.reduce(
               (notLoaded, auxId) => {
                  if (state$.value.activeExercise.modules[auxId]) {
                     return notLoaded;
                  }
                  return {
                     ...notLoaded,
                     [auxId]: true,
                  };
               },
               {}
            )
         )
      ),
      filter((modulesToLoad) => modulesToLoad.length > 0),
      switchMap((modulesToLoad) =>
         merge(...modulesToLoad.map((auxId) => of(loadModule(auxId))))
      )
   );

const loadCurrentAuxiliraryEpic = (action$, state$) =>
   action$.pipe(
      ofType(REFRESH_CURRENT_MODULE),
      filter(
         () =>
            state$.value.activeExercise.modules[
               state$.value.activeExercise.currentModuleId
            ] == null
      ),
      map(() => loadModule(state$.value.activeExercise.currentModuleId))
   );

const updateAfterInitialSyncEpic = (action$, state$) =>
   action$.pipe(
      ofType(INITIAL_SYNC_RESPONSE),
      filter((action) => action.success),
      map((action) => {
         const { exerciseDocument } = state$.value.activeExercise;

         if (
            exerciseDocument.isModule &&
            action.responseData?.modules?.[exerciseDocument.mainExerciseId]?.[
               exerciseDocument.id
            ]
         ) {
            return setProgress(
               action.responseData.modules[exerciseDocument.mainExerciseId][
                  exerciseDocument.id
               ]
            );
         }

         if (
            !exerciseDocument.isModule &&
            action.responseData.exercises?.[exerciseDocument.id]
         ) {
            return setProgress(
               action.responseData.exercises[exerciseDocument.id]
            );
         }
         return null;
      }),
      filter((action) => action != null)
   );

const updatePermissionsEpic = (action$, state$) =>
   action$.pipe(
      ofType(SET_ASSETS_PERMISSIONS_FROM_SESSION_STORAGE),
      switchMap(() => {
         const activeSubscriptions = getSubscriptionsFromState(state$.value);
         const { exerciseDocument } = state$.value.activeExercise;
         const { isModule } = exerciseDocument;
         const exerciseId = isModule
            ? exerciseDocument.mainExerciseId
            : exerciseDocument.id;
         const moduleId = isModule ? exerciseDocument.id : null;

         return ajax({
            url: `${assetsUrl}/${
               activeSubscriptions.exercise ? 'secure' : 'public'
            }/exercise/${exerciseId}${
               moduleId ? `/module/${moduleId}` : ''
            }/exerciseDocument.json`,
            headers: {
               Authorization: `Bearer ${state$.value.appData.permissions.token}`,
            },
            // withCredentials: true,
            // crossDomain: true,
         }).pipe(map(({ response }) => response));
      }),
      switchMap((exerciseDocument) =>
         of(replaceExerciseDocument(exerciseDocument))
      )
   );

export {
   initEpic,
   advanceExerciseEpic,
   prepareNextSessionEpic,
   awaitTutorialEpic,
   nextSessionReadyEpic,
   startNextSessionEpic,
   completeTutorialEpic,
   prepareNextChallengeEpic,
   nextChallengeReadyEpic,
   startNextChallengeEpic,
   submitAnswerEpic,
   compareAnswerEpic,
   completeSessionEpic,
   updateAfterInitialSyncEpic,
   loadModuleEpic,
   selectModulesEpic,
   updatePermissionsEpic,
   loadQueuedModulesEpic,
   loadCurrentAuxiliraryEpic,
   queueModulesOnInitEpic,
   queueModulesOnFirstChallengeEpic,
};
